// Shared language constants for the application
export const SUPPORTED_LANGUAGES = [
  { code: "ar", name: "Arabic" },
  {
    code: "zh-CN",
    name: "Chinese – Simplified (Mandarin, Mainland/Singapore)",
    tooltip:
      'Use this if the caller says "Chinese" or "Mandarin", or is from Mainland China or Singapore.',
  },
  {
    code: "zh-TW",
    name: "Chinese – Traditional (for Cantonese / Hong Kong / Taiwan)",
    tooltip:
      'Use this if the caller says "Cantonese", or is from Hong Kong, Macau, or Taiwan.',
  },
  { code: "en", name: "English" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "hi", name: "Hindi" },
  { code: "ur", name: "Urdu" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "pt", name: "Portuguese" },
  { code: "pa", name: "Punjabi" },
  { code: "ru", name: "Russian" },
  { code: "es", name: "Spanish" },
  { code: "gu", name: "Gujarati" },
  { code: "fil", name: "Filipino" },
  { code: "pl", name: "Polish" },
  { code: "vi", name: "Vietnamese" },
  { code: "ta", name: "Tamil" },
  { code: "tl", name: "Tagalog" },
  { code: "so", name: "Somali" },
  { code: "iu", name: "Inuktitut" },
  { code: "fa", name: "Persian (Farsi)" },
  { code: "prs", name: "Dari" },
  { code: "ti", name: "Tigrinya" },
  { code: "sr", name: "Serbian" },
  { code: "hr", name: "Croatian" },
  { code: "bs", name: "Bosnian" },
];

// SurveyJS supported locales (from FormBuilder.tsx)
export const SURVEYJS_SUPPORTED_LOCALES = [
  "ar",
  "de",
  "en",
  "es",
  "fa",
  "fil",
  "fr",
  "gu",
  "hi",
  "it",
  "ja",
  "ko",
  "pa",
  "pl",
  "pt",
  "ru",
  "ur",
  "so",
  "iu",
  "vi",
  "ta",
  "zh",
  "ti",
  "prs",
  "sr",
  "hr",
  "bs",
];

export const SURVEYJS_LOCALE_NAMES = {
  ar: "Arabic",
  de: "German",
  en: "English",
  es: "Spanish",
  fa: "Persian (Farsi)",
  fil: "Filipino",
  fr: "French",
  gu: "Gujarati",
  hi: "Hindi",
  it: "Italian",
  ja: "Japanese",
  ko: "Korean",
  pa: "Punjabi",
  pl: "Polish",
  prs: "Dari",
  pt: "Portuguese",
  ru: "Russian",
  ti: "Tigrinya",
  ur: "Urdu",
  vi: "Vietnamese",
  ta: "Tamil",
  so: "Somali",
  iu: "Inuktitut",
  zh: "Chinese",
  sr: "Serbian",
  hr: "Croatian",
  bs: "Bosnian",
};

// Languages available for RubiReport settings
// Excludes generic "en", replaces zh-CN/zh-TW with "zh", includes enCa and enUs
export const REPORT_LANGUAGES_MAP = [
  { code: "ar", name: "Arabic" },
  { code: "zh", name: "Chinese" },
  { code: "enCa", name: "English (Canada)" },
  { code: "enUs", name: "English (US)" },
  { code: "fr", name: "French" },
  { code: "frCa", name: "French (Canada)" },
  { code: "de", name: "German" },
  { code: "hi", name: "Hindi" },
  { code: "ur", name: "Urdu" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "pt", name: "Portuguese" },
  { code: "pa", name: "Punjabi" },
  { code: "ru", name: "Russian" },
  { code: "es", name: "Spanish" },
  { code: "gu", name: "Gujarati" },
  { code: "fil", name: "Filipino" },
  { code: "pl", name: "Polish" },
  { code: "vi", name: "Vietnamese" },
  { code: "ta", name: "Tamil" },
  { code: "so", name: "Somali" },
  { code: "iu", name: "Inuktitut" },
  { code: "fa", name: "Persian (Farsi)" },
  { code: "prs", name: "Dari" },
  { code: "ti", name: "Tigrinya" },
  { code: "sr", name: "Serbian" },
  { code: "hr", name: "Croatian" },
  { code: "bs", name: "Bosnian" },
];

/** True for English variants used in report/survey group language lists. */
export function isEnglishCode(code: string): boolean {
  return code === "en" || code === "enCa" || code === "enUs";
}

/** Display name for a code in {@link REPORT_LANGUAGES_MAP}, or the code if unknown. */
export function getReportLanguageDisplayName(code: string): string {
  return REPORT_LANGUAGES_MAP.find((lang) => lang.code === code)?.name ?? code;
}

/**
 * Converts internal language codes (e.g. frCa, enCa) to BCP 47 locale format
 * for the translate API (e.g. fr-ca, en-ca). Azure Translator expects locale
 * format; stored translation keys remain in internal format.
 */
export function languageCodeToTranslateLocale(code: string): string {
  return code.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Resolves the locale to send to the translate API when translating from the
 * Form Builder. When the agency has a regional variant (e.g. French Canada),
 * prefer that so Azure returns the right variant (e.g. fr-ca).
 */
export function surveyJsLocaleToTranslateTarget(
  surveyJsLocale: string,
  supportedLanguages: string[] | undefined,
): string {
  if (!supportedLanguages?.length) {
    return languageCodeToTranslateLocale(surveyJsLocale);
  }
  if (surveyJsLocale === "fr") {
    if (supportedLanguages.includes("frCa")) return "fr-ca";
    if (supportedLanguages.includes("fr")) return "fr";
    return "fr";
  }

  return languageCodeToTranslateLocale(surveyJsLocale);
}
